import dayjs from 'dayjs';
import { type ButtonHTMLAttributes, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { Availability } from '@/models';

interface TimeSlotProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  label: string;
  selected: boolean;
}

interface TimeSlotPickerProps {
  availabilities: Availability[];
  selectedAvailabilityId: string;
  loading: boolean;
  onSelect: (availability: Availability) => void;
}

const TimeSlot: FC<TimeSlotProps> = ({
  label,
  selected,
  disabled = false,
  className = '',
  ...props
}) => {
  const stateClass = selected
    ? 'bg-primary text-white'
    : disabled
      ? 'bg-bg-disabled text-border-inactive cursor-not-allowed'
      : 'border-border-inactive text-secondary-text hover:border-border-hover border';

  return (
    <button
      type="button"
      disabled={disabled}
      className={`flex h-8 w-full items-center justify-center rounded px-2 py-0.5 leading-5 transition-all duration-200 ${stateClass} ${className}`}
      {...props}
    >
      {label}
    </button>
  );
};

const TimeSlotPicker: FC<TimeSlotPickerProps> = ({
  availabilities,
  selectedAvailabilityId,
  loading,
  onSelect,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="animate-in fade-in grid w-full grid-cols-3 gap-2.5 duration-300 md:grid-cols-6">
        {Array.from({ length: 24 }).map((_, index) => (
          <div
            key={index}
            className="h-8 w-full animate-pulse rounded bg-gray-200"
          />
        ))}
      </div>
    );
  }

  if (availabilities.length === 0) {
    return (
      <div className="animate-in fade-in flex w-full items-center justify-center rounded-lg bg-gray-50 py-8 duration-500">
        <p className="text-secondary-text leading-5">
          {t('dateTime.noSlotsAvailable')}
        </p>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in grid w-full grid-cols-3 gap-2.5 duration-500 md:grid-cols-6">
      {availabilities.map((availability) => {
        const timeStr = dayjs(availability.availabilityId).format('HH:mm');

        return (
          <TimeSlot
            key={availability.availabilityId}
            label={timeStr}
            selected={selectedAvailabilityId === availability.availabilityId}
            disabled={!availability.available}
            onClick={() => {
              onSelect(availability);
            }}
          />
        );
      })}
    </div>
  );
};

export default TimeSlotPicker;
